/**
 * AddToCampaignDialog — select a campaign to assign one or more leads to.
 *
 * Used from three places: the lead detail panel (single lead), the Lead
 * Pipeline bulk-selection bar (many leads), and right after an import from
 * Find Leads (the freshly-created lead ids). All three funnel through the
 * same `leadIds` array so the copy and the mutation stay identical.
 */

import { useState } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { useCampaigns, useAddLeadsToCampaign } from '@/features/campaigns/use-campaigns'
import type { Lead } from './types'

type AddToCampaignDialogProps = {
  /** Leads to assign. One entry = single-lead flow, many = bulk. */
  readonly leadIds: readonly string[]
  /** Name shown when assigning a single, known lead (detail panel). */
  readonly leadName?: string
  /**
   * Full lead objects for the ids being assigned, when the caller has them.
   * Used to say which campaign each lead is in today and warn about moves.
   * Callers with only fresh ids (post-import) can omit this — brand-new
   * leads have no campaign to move out of.
   */
  readonly leads?: readonly Lead[]
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /** Called after the leads are added, with the chosen campaign id. */
  readonly onAdded?: (campaignId: string) => void
}

export function AddToCampaignDialog({ leadIds, leadName, leads, open, onOpenChange, onAdded }: AddToCampaignDialogProps) {
  const { data: campaigns = [] } = useCampaigns()
  const addLeads = useAddLeadsToCampaign()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const count = leadIds.length
  // Filter to active/draft campaigns (not completed)
  const availableCampaigns = campaigns.filter((c) => c.status !== 'completed')

  const subject =
    count === 1 && leadName
      ? leadName
      : `${count} lead${count === 1 ? '' : 's'}`

  // A lead can only be in one campaign, so assigning an already-campaigned
  // lead MOVES it. Tell the user exactly which campaigns are affected.
  const idSet = new Set(leadIds)
  const selectedLeads = (leads ?? []).filter((l) => idSet.has(l.id))
  const singleLead = selectedLeads.length === 1 ? selectedLeads[0] : undefined
  const movingLeads = selectedLeads.filter(
    (l) => l.campaign_id && l.campaign_id !== selectedId,
  )
  const movingByCampaign = new Map<string, number>()
  for (const l of movingLeads) {
    const name = l.campaign ?? 'another campaign'
    movingByCampaign.set(name, (movingByCampaign.get(name) ?? 0) + 1)
  }
  const movingSummary = [...movingByCampaign.entries()]
    .map(([name, n]) => `${n} in ${name}`)
    .join(', ')

  async function handleAdd() {
    if (!selectedId || count === 0) return
    await addLeads.mutateAsync({ campaignId: selectedId, leadIds })
    onAdded?.(selectedId)
    onOpenChange(false)
    setSelectedId(null)
  }

  function handleOpenChange(next: boolean) {
    if (addLeads.isPending) return
    if (!next) setSelectedId(null)
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add to Campaign</DialogTitle>
          <DialogDescription>
            Assign {subject} to a campaign.
          </DialogDescription>
        </DialogHeader>

        {availableCampaigns.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No campaigns available. Create a campaign first.
          </p>
        ) : (
          <div className="flex flex-col gap-1.5 max-h-60 overflow-y-auto">
            {availableCampaigns.map((campaign) => (
              <button
                key={campaign.id}
                type="button"
                onClick={() => setSelectedId(campaign.id)}
                className={[
                  'flex items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                  selectedId === campaign.id
                    ? 'border-primary bg-primary/5'
                    : 'border-input hover:bg-muted/50',
                ].join(' ')}
              >
                <div>
                  <p className="font-medium">{campaign.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {campaign.leads_count} leads &middot; {campaign.status}
                  </p>
                </div>
                {singleLead?.campaign_id === campaign.id && (
                  <Badge variant="secondary" className="shrink-0 text-[10px]">
                    Current campaign
                  </Badge>
                )}
              </button>
            ))}
          </div>
        )}

        {movingLeads.length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              {movingLeads.length === 1
                ? `${singleLead && movingLeads[0] === singleLead ? 'This lead' : '1 selected lead'} is already in ${movingLeads[0]?.campaign ?? 'another campaign'} and will be moved.`
                : `${movingLeads.length} selected leads are already in a campaign (${movingSummary}) and will be moved.`}
            </span>
          </div>
        )}

        {addLeads.error && (
          <p className="text-sm text-destructive">
            {addLeads.error instanceof Error ? addLeads.error.message : 'Failed to add'}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={addLeads.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleAdd()} disabled={!selectedId || count === 0 || addLeads.isPending}>
            {addLeads.isPending ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Adding...</>
            ) : (
              `Add ${count === 1 ? 'lead' : `${count} leads`}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
